import { useEffect, useState } from "react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
  LineChart, Line,
} from "recharts";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDownRight, Send, Settings as SettingsIcon, ImageOff, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  async function sendDigest() {
    setSending(true);
    try {
      await api.post("/notifications/digest/send");
      toast.success("Digest sent to Telegram.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  }

  async function downloadExcel() {
    try {
      const res = await api.get("/exports/inventory.xlsx", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `closet-inventory-${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Inventory exported.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="dashboard-page">
      <header className="mb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">/ DASHBOARD · CONTROL ROOM</div>
          <h1 className="section-title text-5xl lg:text-6xl">The State<br />of the Closet.</h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-xl">
            Live snapshot of every SKU, every size, every drop. Numbers don’t lie — these don’t either.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <button onClick={downloadExcel} className="brut-btn px-4 py-2.5 inline-flex items-center gap-2" data-testid="download-excel-btn">
            <FileDown size={16} /> Download Excel
          </button>
          <button onClick={sendDigest} disabled={sending} className="brut-btn-outline px-4 py-2.5 inline-flex items-center gap-2 disabled:opacity-60" data-testid="send-digest-btn">
            <Send size={16} /> {sending ? "Sending…" : "Send Digest Now"}
          </button>
          {user?.role === "admin" && (
            <Link to="/settings" className="brut-btn-outline px-4 py-2 inline-flex items-center gap-2 text-xs" data-testid="link-settings">
              <SettingsIcon size={14} /> Notification Settings
            </Link>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <Kpi label="Total Products" value={stats?.total_products ?? "—"} suffix="SKUs" testid="kpi-products" />
        <Kpi label="Stock On Hand" value={stats?.total_units ?? "—"} suffix="UNITS" testid="kpi-units" />
        <Kpi label="Stock Value" value={stats != null ? fmtINR(stats.total_stock_value) : "—"} testid="kpi-value" raw />
        <Kpi
          label="Low-Stock Sizes"
          value={stats?.low_stock_count ?? "—"}
          suffix="VARIANTS"
          danger={(stats?.low_stock_count ?? 0) > 0}
          testid="kpi-low-stock"
        />
      </div>

      {(stats && (stats.out_size_alerts?.length > 0 || stats.low_size_alerts?.length > 0)) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {stats.out_size_alerts?.length > 0 && (
            <AlertList
              testid="out-size-alerts"
              title="Out of Stock — by Size"
              tone="alert"
              items={stats.out_size_alerts}
              renderRight={() => <span className="brut-badge alert">0 LEFT</span>}
            />
          )}
          {stats.low_size_alerts?.length > 0 && (
            <AlertList
              testid="low-size-alerts"
              title="Low Stock — by Size"
              tone="warning"
              items={stats.low_size_alerts}
              renderRight={(it) => (
                <div className="text-right">
                  <div className="font-mono-ds font-bold text-lg leading-none">{it.stock} <span className="text-xs">left</span></div>
                  <div className="font-mono-ds text-[10px] text-zinc-500">thr {it.threshold} · short {it.shortfall}</div>
                </div>
              )}
            />
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="brut-card p-6" data-testid="chart-by-category">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">FIG. 01</div>
              <h2 className="font-display font-black text-xl tracking-tight">Stock by Category</h2>
            </div>
            <span className="brut-badge">UNITS</span>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={stats?.stock_by_category || []}>
                <CartesianGrid stroke="#0a0a0a" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="name" tick={{ fontFamily: "IBM Plex Mono", fontSize: 10 }} stroke="#0a0a0a" />
                <YAxis tick={{ fontFamily: "IBM Plex Mono", fontSize: 10 }} stroke="#0a0a0a" />
                <Tooltip contentStyle={{ border: "2px solid #0a0a0a", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                <Bar dataKey="stock" fill="#0a0a0a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="brut-card p-6" data-testid="chart-trend">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">FIG. 02</div>
              <h2 className="font-display font-black text-xl tracking-tight">Movements (14d)</h2>
            </div>
            <div className="flex gap-2">
              <span className="brut-badge success">IN</span>
              <span className="brut-badge alert">OUT</span>
            </div>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={stats?.movements_trend || []}>
                <CartesianGrid stroke="#0a0a0a" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="date" tick={{ fontFamily: "IBM Plex Mono", fontSize: 10 }} stroke="#0a0a0a" />
                <YAxis tick={{ fontFamily: "IBM Plex Mono", fontSize: 10 }} stroke="#0a0a0a" />
                <Tooltip contentStyle={{ border: "2px solid #0a0a0a", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                <Line type="monotone" dataKey="in" stroke="#00C853" strokeWidth={3} dot={{ r: 4, fill: "#00C853", stroke: "#0a0a0a", strokeWidth: 2 }} />
                <Line type="monotone" dataKey="out" stroke="#FF3333" strokeWidth={3} dot={{ r: 4, fill: "#FF3333", stroke: "#0a0a0a", strokeWidth: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="brut-card p-6" data-testid="recent-movements">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">FIG. 03</div>
            <h2 className="font-display font-black text-xl tracking-tight">Recent Movements</h2>
          </div>
          <Link to="/movements" className="brut-badge hover:bg-black hover:text-white">View all →</Link>
        </div>
        <table className="brut-table">
          <thead>
            <tr>
              <th>When</th><th>Type</th><th>Product</th><th>Size</th><th>Qty</th><th>Reason</th><th>By</th>
            </tr>
          </thead>
          <tbody>
            {(stats?.recent_movements || []).map((m) => (
              <tr key={m.id}>
                <td className="font-mono-ds text-xs">{new Date(m.created_at).toLocaleString()}</td>
                <td>
                  {m.type === "IN"
                    ? <span className="brut-badge success"><ArrowDownRight size={10} className="mr-1" />IN</span>
                    : <span className="brut-badge alert"><ArrowUpRight size={10} className="mr-1" />OUT</span>}
                </td>
                <td className="font-medium">{m.product_name}<div className="font-mono-ds text-[10px] text-zinc-500">{m.product_sku}</div></td>
                <td className="font-mono-ds">{m.size}</td>
                <td className="font-mono-ds font-bold">{m.quantity}</td>
                <td className="font-mono-ds text-xs">{m.reason}</td>
                <td className="text-xs">{m.user_name}</td>
              </tr>
            ))}
            {(!stats || stats.recent_movements?.length === 0) && (
              <tr><td colSpan={7} className="text-center py-8 text-zinc-500 font-mono-ds text-xs uppercase">No movements yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AlertList({ title, items, tone, renderRight, testid }) {
  return (
    <div className="brut-card p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className={`font-mono-ds text-[10px] tracking-widest uppercase ${tone === "alert" ? "text-[#FF3333]" : "text-zinc-500"}`}>
            {tone === "alert" ? "CRITICAL" : "ATTENTION"}
          </div>
          <h2 className="font-display font-black text-xl tracking-tight">{title}</h2>
        </div>
        <span className={`brut-badge ${tone === "alert" ? "alert" : "warning"}`}>{items.length}</span>
      </div>
      <div className="divide-y-2 divide-black max-h-[280px] overflow-y-auto">
        {items.slice(0, 12).map((it, i) => (
          <Link
            key={`${it.product_id}-${it.size}-${i}`}
            to={`/products/${it.product_id}`}
            className="flex items-center gap-3 py-3 hover:bg-zinc-50 px-1"
            data-testid={`alert-row-${tone}`}
          >
            <div className="w-10 h-10 border-2 border-black bg-zinc-100 overflow-hidden flex-shrink-0">
              {it.image
                ? <img src={fileUrl(it.image)} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-zinc-400"><ImageOff size={14} /></div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm truncate">{it.product_name}</div>
              <div className="font-mono-ds text-[10px] text-zinc-500">
                {it.product_sku} · Size <b className="text-zinc-900">{it.size}</b>
              </div>
            </div>
            {renderRight(it)}
          </Link>
        ))}
        {items.length > 12 && (
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 py-2 text-center">
            …and {items.length - 12} more — see Low Stock page
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, suffix, danger, testid, raw }) {
  return (
    <div className={`brut-card p-6 ${danger ? "bg-[#FFB800]" : ""}`} data-testid={testid}>
      <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-700">{label}</div>
      <div className="kpi-num mt-3">{value}{!raw && suffix ? "" : ""}</div>
      {suffix && <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-700 mt-2">{suffix}</div>}
    </div>
  );
}
